import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useDeviceLinkContextProvider } from 'features/device/providers/device-link-provider/device-link-provider';
import { ResidentValidateTransmitterResultType } from 'features/residents/models/resident-validate-transmitter-result-type';
import ValidateTransmitterResponse from 'features/residents/models/validate-transmitter-response';
import CreateLinkableTransmitterCommand from 'features/residents/resident-settings/components/resident-setting-details/components/devices-accordion/components/details-devices/components/linkable-transmitter-steps/domain/create-linkable-transmitter-command';
import { LinkableTransmitterValidationPopupState } from 'features/residents/resident-settings/components/resident-setting-details/components/devices-accordion/components/details-devices/components/linkable-transmitter-steps/providers/states/linkable-transmitter-validation-popup-state';
import { useCreateTransmitter, useValidateTransmitter } from 'features/residents/resident-settings/providers/resident-queries';

type Props = {
  form: UseFormReturn<CreateLinkableTransmitterCommand>;
  validationPopupState: LinkableTransmitterValidationPopupState;
  onTransmitterAdded: (createdDeviceId: string) => void;
};

type LinkableTransmitterStepsActions = {
  validateTransmitter: () => Promise<boolean>;
  addTransmitter: () => Promise<void>;
};

const useLinkableTransmitterSteps = ({ form, validationPopupState, onTransmitterAdded }: Props): LinkableTransmitterStepsActions => {
  const { t: tDeviceTypes } = useTranslation('deviceTypes');
  const { organisationUnitId } = useDeviceLinkContextProvider();
  const { mutateAsync: validateTransmitterAsync } = useValidateTransmitter();
  const { mutateAsync: createTransmitterAsync } = useCreateTransmitter();

  const validateTransmitter = async (): Promise<boolean> => {
    const externalId = form.getValues('externalId')?.trim();
    const deviceType = form.getValues('type');

    try {
      await validateTransmitterAsync({ organisationUnitId, externalId, deviceType });
      return true;
    } catch (error: any) {
      const responseData: ValidateTransmitterResponse = error?.response?.data ?? {
        validationResultType: ResidentValidateTransmitterResultType.Unknown,
      };
      validationPopupState.handleValidationResult(responseData);
      return false;
    }
  };

  const addTransmitter = async (): Promise<void> => {
    const externalId = form.getValues('externalId')?.trim();
    const deviceType = form.getValues('type');
    const deviceName = `${externalId} ${tDeviceTypes(deviceType)}`;

    try {
      const response = await createTransmitterAsync({ organisationUnitId, externalId, deviceType, deviceName });
      if (response.createdDeviceId) {
        onTransmitterAdded(response.createdDeviceId);
      }
    } catch (error: any) {
      const responseData: ValidateTransmitterResponse = error?.response?.data ?? {
        validationResultType: ResidentValidateTransmitterResultType.Unknown,
      };
      validationPopupState.handleValidationResult(responseData);
    }
  };

  return { validateTransmitter, addTransmitter };
};

export default useLinkableTransmitterSteps;
